/**
 * Dev-time diagnostic for env-gated feature flags.
 *
 * `.env.development.local` is gitignored, so a git worktree never receives it.
 * When it is missing, `process.env.REACT_APP_*` is UNDEFINED and every flag that
 * defaults to OFF resolves to false — the "Login with Google" button just is not
 * there, with nothing in the console to say why. This turns that silence into a
 * named cause.
 *
 * Deliberately narrow: it only reports vars that are UNSET. An explicit
 * "false" is a decision, not a misconfiguration, and is never warned about.
 * Vars whose unset default is ON (marketplace) are not listed either — losing
 * them is not silent.
 */

export type EnvGatedFlag = {
  envVar: string;
  surface: string;
};

/** Flags that silently resolve to OFF when their env var is absent. */
export const ENV_GATED_FLAGS: EnvGatedFlag[] = [
  {
    envVar: 'REACT_APP_GOOGLE_AUTH_ENABLED',
    surface: 'the "Login with Google" button'
  },
  {
    envVar: 'REACT_APP_ENABLE_CIRCUIT_BREAKER',
    surface: 'the API circuit breaker'
  }
];

/**
 * Reads the gated vars as static `process.env.X` accesses, co-located with the
 * list above so the two cannot drift. CRA's DefinePlugin only substitutes
 * literal member accesses — `process.env[key]` would silently read undefined.
 */
export const readEnvGatedFlagValues = (): Record<
  string,
  string | undefined
> => ({
  REACT_APP_GOOGLE_AUTH_ENABLED: process.env.REACT_APP_GOOGLE_AUTH_ENABLED,
  REACT_APP_ENABLE_CIRCUIT_BREAKER: process.env.REACT_APP_ENABLE_CIRCUIT_BREAKER
});

const isUnset = (value: string | undefined): boolean =>
  value === undefined || String(value).trim() === '';

export const collectUnsetEnvGatedFlags = (
  env: Record<string, string | undefined>
): EnvGatedFlag[] =>
  ENV_GATED_FLAGS.filter((flag) => isUnset(env[flag.envVar]));

export const buildMissingEnvWarning = (missing: EnvGatedFlag[]): string =>
  [
    `[feature-flags] ${missing
      .map((flag) => flag.envVar)
      .join(', ')} is not set, so ${missing
      .map((flag) => flag.surface)
      .join(' and ')} will be hidden.`,
    'This is almost always a missing .env.development.local — it is gitignored,',
    'so a git worktree does not inherit it. Fix: `yarn setup:worktree` from the',
    'worktree root. To turn a flag off on purpose, set it to "false" explicitly.'
  ].join(' ');

/**
 * Logs one warning naming the missing env file. No-op outside development, and
 * no-op when every gated var is explicitly set (to either value).
 *
 * @returns the message that was logged, or null when nothing was logged.
 */
export const warnOnUnsetEnvGatedFlags = ({
  nodeEnv,
  env,
  logger = console
}: {
  nodeEnv: string | undefined;
  env: Record<string, string | undefined>;
  logger?: Pick<Console, 'warn'>;
}): string | null => {
  if (nodeEnv !== 'development') return null;

  const missing = collectUnsetEnvGatedFlags(env);
  if (missing.length === 0) return null;

  const message = buildMissingEnvWarning(missing);
  logger.warn(message);
  return message;
};
